"use client"

import { useState, useEffect, useMemo } from "react";
import { Card, Form, FloatingLabel, Button } from "react-bootstrap";
import { showLoading } from "react-global-loading";
import { toast } from "react-toastify";
import { Link, useLocation } from "react-router-dom";

import * as myApi from "../Api";
import {
    validateFile,
    validatePaperTitle,
    validateAuthorName,
    validatePaperSubmissionForm,
    validatePhone,
    validateEmail,
    validateText
} from "../../utils/formValidation";

// Helper function to get API base URL
const getBaseUrl = () => {
    return window.env?.API_URL || process.env.REACT_APP_API_URL || "http://localhost:3001";
};

// Helper component to show existing file preview
const ExistingFilePreview = ({ filePath, fileName, label, editMode }) => {
    if (!editMode || !filePath || filePath === '-' || filePath === '-') {
        return null;
    }
    
    const baseUrl = getBaseUrl();
    const fileUrl = `${baseUrl}/${filePath}`;
    
    return (
        <div className="mt-2">
            <small className="text-muted">{label}: </small>
            <a 
                href={fileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary text-decoration-none"
            >
                {fileName}
            </a>
        </div>
    );
};

const PaperCreate = () => {

    const [inTheme, setInTheme] = useState('General');
    const [inSubTheme, setInSubTheme] = useState('');
    const [inCategory, setInCategory] = useState('');
    const [inGroupCategory, setInGroupCategory] = useState('');
    
    // Controlled form fields - directly tied to form inputs
    const [formValues, setFormValues] = useState({
        judul: '',
        paper_file: null,
        cv_file: null,
        pernyataan_file: null,
        pddikti_file: null,
        nama_2: '',
        email_2: '',
        instansi_2: '',
        address_2: '',
        phone_2: '',
        nama_3: '',
        email_3: '',
        instansi_3: '',
        address_3: '',
        phone_3: ''
    });
    
    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    const [paperCode, setPaperCode] = useState('');
    const [existingPaperData, setExistingPaperData] = useState(null);
    const [isLoadingPaper, setIsLoadingPaper] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const [paperLoaded, setPaperLoaded] = useState(false); // Used to force re-render after loading
    const [agreementChecked, setAgreementChecked] = useState(false);
    
    // Get current path for form reset
    const location = useLocation();
    
    // Validation state
    const [validationErrors, setValidationErrors] = useState({});
    const [fileErrors, setFileErrors] = useState({});
    const [formData, setFormData] = useState({
        email: '',
        phone: '',
        organization: '',
        judul: ''
    });

    const userCategory = {
        'Umum': [
            { id: '0', value: 'Individu'},
            { id: '1', value: 'Group'}
        ],
        'Mahasiswa': [
            { id: '0', value: 'Individu'},
            { id: '1', value: 'Group'}
        ],
    }
    const categoryLabels = {
        'Umum': 'Umum (S2 dan S3, Dosen, Praktisi, ASN, Pelaku Usaha dan Umum)',
        'Mahasiswa': 'Mahasiswa (D3, D4 dan S1)',
    }
    const categoryBase = {
        'General': [
            { id: '1', text: 'Peningkatan Daya Saing Industri, Hilirisasi, dan Investasi' },
            { id: '2', text: 'Ketahanan Pangan Terintegrasi dan Stabilitas Harga' },
            { id: '3', text: 'Perdagangan, Konektivitas, dan Integrasi Ekonomi Regional' },
            { id: '4', text: 'Pariwisata dan Sumber Ekonomi Baru' },
            { id: '5', text: 'Pengembangan UMKM, Inklusi Keuangan, dan Inovasi Pembiayaan' },
            { id: '6', text: 'Kesiapan Pasar Tenaga Kerja dan Kesejahteraan Sosial' }
        ],
        // 'Modeling': [
        //     { id: '7', text: 'Model Proyeksi/Forecasting/Determinan Pertumbuhan Ekonomi Jawa Timur Dan Turunannya (Sisi Permintaan).' },
        //     { id: '8', text: 'Model Proyeksi/Forecasting/Determinan Pertumbuhan Ekonomi Jawa Timur Dan Turunannya (Sisi Penawaran).' },
        //     { id: '9', text: 'Simulasi Berbagai Dampak Isu Strategis Terhadap Ketahanan Perekonomian Jawa Timur.' },
        //     { id: '10', text: 'Simulasi Berbagai Opsi Kebijakan, Baik Kebijakan Moneter, Makroprudensial, Mikroprudensial, Fiskal, Maupun Kebijakan Pemerintah Pusat Dan Daerah, Serta Kebijakan Negara Lain Terhadap Perekonomian Jawa Timur.' },
        //     { id: '11', text: 'Fundamental Pertumbuhan Ekonomi Jawa Timur Saat Ini dan Ke Depan.' }
        // ],
        // 'Sharia': [
        //     { id: '12', text: 'Food' },
        //     { id: '13', text: 'Fashion'},
        //     { id: '14', text: 'Finance (meliputi juga instrumen keuangan syariah)'},
        //     { id: '15', text: 'Integrated Farming'},
        //     { id: '16', text: 'Renewable Energy'},
        //     { id: '17', text: 'Fundutainment (industri kreatif meliputi aplikasi, games, film, musik, arsitektur, desain dan seni pertunjukan)'},
        //     { id: '18', text: 'Funtrepreneur (jasa/properti/socialpreneur/travel dll)'}
        // ],
    }

    const loadUserData = async() => {
        try {
            const response = await myApi.get('/user/profile/');
            if (response.data.data !== undefined) {
                const userData = {
                    email: response.data.data.email || '',
                    phone: response.data.data.phone || '',
                    organization: response.data.data.organization || '',
                    judul: ''
                };
                setFormData(userData);
                
                const useridEl = document.getElementById('userid_code');
                const nameEl = document.getElementById('name');
                const emailEl = document.getElementById('email');
                const phoneEl = document.getElementById('phone');
                const orgEl = document.getElementById('organization');
                
                if (useridEl) useridEl.value = response.data.data.userid_code;
                if (nameEl) nameEl.value = response.data.data.name;
                if (emailEl) emailEl.value = response.data.data.email;
                if (phoneEl) phoneEl.value = response.data.data.phone;
                if (orgEl) orgEl.value = response.data.data.organization;
            }
        } catch (error) {
            toast(error.message, { type: "error" });
            console.error(error);
        }
    }

    const paperSubmissionExpiration = async() => {
        try {
            const response = await myApi.get('/deadline');
            if (response.data.result) {
                const deadlineInfo = response.data.result;
                const dateNow = new Date();
                const dateLimit = new Date(deadlineInfo.general_deadline);

                // Only block new submissions when deadline is passed, allow editing
                if (!editMode && dateNow > dateLimit) {
                    toast('Submission is closed', { type: "error" });
                    setTimeout(() => {window.location.href = '/paper/list';}, 3000);
                }
            }
        } catch (error) {
            console.error('Error fetching deadline:', error);
            // Don't block the page on deadline check error
        }
    }

    useEffect(() => {
        loadUserData();
        
        // Set form dropdown values from state
        const themeEl = document.getElementById('s-theme');
        const subThemeEl = document.getElementById('s-subtheme');
        const categoryEl = document.getElementById('s-category');
        const groupEl = document.getElementById('s-group');
        
        if (themeEl) themeEl.value = inTheme;
        if (subThemeEl) subThemeEl.value = inSubTheme;
        if (categoryEl) categoryEl.value = inCategory;
        if (groupEl) groupEl.value = inGroupCategory;
        
        // Ensure form is ready for interaction in both modes
        setPaperLoaded(true);
        if (editMode && existingPaperData) {
            setAgreementChecked(true);
        }
    }, [inTheme, inSubTheme, inCategory, inGroupCategory, editMode, existingPaperData]);

    // When existingPaperData changes, populate all form values
    useEffect(() => {
        if (!existingPaperData) return;
        
        console.log('Populating form with paper data:', existingPaperData);
        
        // Update dropdown states
        const paperType = existingPaperData.paper_type === 'General Paper' ? 'General' : existingPaperData.paper_type;
        setInTheme(paperType);
        setInSubTheme(existingPaperData.sub_theme || '');
        setInCategory(existingPaperData.category || '');
        setInGroupCategory(existingPaperData.participation_type || '');
        
        // Update controlled form values - including author fields
        setFormValues(prev => ({
            ...prev,
            judul: existingPaperData.title || '',
            nama_2: existingPaperData.name_2 || '',
            email_2: existingPaperData.email_2 || '',
            organisasi_2: existingPaperData.organization_2 || '',
            address_2: existingPaperData.address_2 || '',
            phone_2: existingPaperData.phone_2 || '',
            nama_3: existingPaperData.name_3 || '',
            email_3: existingPaperData.email_3 || '',
            organisasi_3: existingPaperData.organization_3 || '',
            address_3: existingPaperData.address_3 || '',
            phone_3: existingPaperData.phone_3 || '',
            // Also populate instansi (used in form)
            instansi_2: existingPaperData.organization_2 || '',
            instansi_3: existingPaperData.organization_3 || ''
        }));
        
        // Force form re-render
        setFormKey(prev => prev + 1);
        setPaperLoaded(true);
        
        toast('Editing paper: ' + paperCode, { type: "info" });
        
    }, [existingPaperData]);

    // Handle edit mode - fetch and populate form
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        
        if (code) {
            console.log('Edit mode detected, code:', code);
            setPaperCode(code);
            setEditMode(true);
            
            // Fetch paper data
            fetchPaperDataDirect(code);
        } else {
            // Reset form when navigating to /paper/add (create mode)
            console.log('Create mode - resetting form');
            setEditMode(false);
            setPaperCode('');
            setExistingPaperData(null);
            setPaperLoaded(false);
            setInTheme('General');
            setInSubTheme('');
            setInCategory('');
            setInGroupCategory('');
            setFormValues({
                judul: '',
                paper_file: null,
                cv_file: null,
                pernyataan_file: null,
                pddikti_file: null
            });
            setFormKey(prev => prev + 1);
        }
    }, [location.pathname]);

    // Deadline check - only run after editMode is determined
    useEffect(() => {
        // Skip deadline check in edit mode
        if (editMode) return;
        
        paperSubmissionExpiration();
    }, [editMode]);

    // Direct function to fetch and populate paper
    const fetchPaperDataDirect = async (code) => {
        setIsLoadingPaper(true);
        try {
            const response = await myApi.post('/paper/search/', { 
                keyword: code,
                filter: 'kodepaper',
                search_type: 'search',
                page: 1,
                count: 1,
                status_filter: ''
            });
            
            console.log('API Response:', response);
            
            if (response.data && response.data.data && response.data.data.data && response.data.data.data.length > 0) {
                const paper = response.data.data.data[0];
                console.log('Paper found:', paper);
                
                // Check if paper is already submitted - redirect if so
                if (paper.submit_status === 'submit') {
                    toast('Cannot edit paper that has already been submitted', { type: "error" });
                    setTimeout(() => {
                        window.location.href = '/paper/list/';
                    }, 1500);
                    return;
                }
                
                // Store the paper data - the useEffect will handle population
                setExistingPaperData(paper);
                
            } else {
                console.log('No paper found');
                toast('Paper not found', { type: "error" });
            }
        } catch (error) {
            console.error('Error fetching paper:', error);
            toast('Error loading paper data: ' + error.message, { type: "error" });
        }
        setIsLoadingPaper(false);
    }

    // Keep fetchPaperData for backward compatibility
    const fetchPaperData = async (code) => {
        // This is now handled by fetchPaperDataDirect
    }

    // Validation functions
    const validateTitleField = (title) => {
        const validation = validatePaperTitle(title);
        setValidationErrors(prev => ({
            ...prev,
            judul: validation.valid ? '' : validation.message
        }));
        return validation.valid;
    };

    const validateFileField = (file, fieldName, fieldNameLabel) => {
        const validation = validateFile(file);
        setFileErrors(prev => ({
            ...prev,
            [fieldName]: validation.valid ? '' : validation.message
        }));
        return validation.valid;
    };

    const validateAuthorField = (value, fieldName, fieldNameLabel) => {
        const validation = validateAuthorName(value);
        setValidationErrors(prev => ({
            ...prev,
            [fieldName]: validation.valid ? '' : validation.message
        }));
        return validation.valid;
    };

    const isFormValid = () => {
        const form = document.getElementById('form-create');
        if (!form) return false;
        
        const formData = new FormData(form);
        const validation = validatePaperSubmissionForm(formData, inGroupCategory === 'Group', inCategory);
        
        if (!validation.valid) {
            validation.errors.forEach(error => {
                toast(error, { type: "error" });
            });
            return false;
        }
        
        return true;
    };

    const handleSubmit = async(e) => {
        e.preventDefault();
        
        // Validate entire form first (skip in edit mode as files are optional)
        if (!editMode && !isFormValid()) {
            showLoading(false);
            return;
        }
        
        showLoading(true);
        const formData = new FormData(e.target);
        
        let themeAdjust = formData.get('jenis_paper_text') === 'General' ? formData.get('jenis_paper_text') + ' Paper' : formData.get('jenis_paper_text');
        formData.set('jenis_paper_text', themeAdjust);

        try {
            // Determine if creating new or editing existing
            let fetchUrl;
            
            if (editMode && paperCode) {
                // Use dedicated editPaper endpoint
                fetchUrl = '/editPaper';
                
                // Build request body - use original paper_code to find the paper
                const requestData = new FormData();
                requestData.append('paper_code', paperCode);
                requestData.append('paper_type', existingPaperData?.paper_type || formData.get('jenis_paper_text'));
                requestData.append('title', formData.get('judul'));
                requestData.append('participation_type', formData.get('keikutsertaan'));
                requestData.append('sub_theme', formData.get('sub_tema_text'));
                requestData.append('category', formData.get('kategori'));
                
                // Only append files if provided
                if (formData.get('paper_file') && formData.get('paper_file').size > 0) {
                    requestData.append('paper_file', formData.get('paper_file'));
                }
                if (formData.get('cv_file') && formData.get('cv_file').size > 0) {
                    requestData.append('cv_file', formData.get('cv_file'));
                }
                if (formData.get('pernyataan_file') && formData.get('pernyataan_file').size > 0) {
                    requestData.append('pernyataan_file', formData.get('pernyataan_file'));
                }
                if (formData.get('pddikti_file') && formData.get('pddikti_file').size > 0) {
                    requestData.append('pddikti_file', formData.get('pddikti_file'));
                }

                // Append group author fields (always send to allow clearing)
                requestData.append('name_2', formData.get('nama_2') || '');
                requestData.append('email_2', formData.get('email_2') || '');
                requestData.append('organization_2', formData.get('instansi_2') || '');
                requestData.append('address_2', formData.get('address_2') || '');
                requestData.append('phone_2', formData.get('phone_2') || '');
                requestData.append('name_3', formData.get('nama_3') || '');
                requestData.append('email_3', formData.get('email_3') || '');
                requestData.append('organization_3', formData.get('instansi_3') || '');
                requestData.append('address_3', formData.get('address_3') || '');
                requestData.append('phone_3', formData.get('phone_3') || '');

                // Append co-author files if provided
                if (formData.get('cv_2_file') && formData.get('cv_2_file').size > 0) {
                    requestData.append('cv_2_file', formData.get('cv_2_file'));
                }
                if (formData.get('cv_3_file') && formData.get('cv_3_file').size > 0) {
                    requestData.append('cv_3_file', formData.get('cv_3_file'));
                }
                if (formData.get('pddikti_2_file') && formData.get('pddikti_2_file').size > 0) {
                    requestData.append('pddikti_2_file', formData.get('pddikti_2_file'));
                }
                if (formData.get('pddikti_3_file') && formData.get('pddikti_3_file').size > 0) {
                    requestData.append('pddikti_3_file', formData.get('pddikti_3_file'));
                }

                    myApi.post(fetchUrl, requestData, "multipart/form-data")
                .then((response) => {
                    showLoading(false);
                    if (response.data.status === 'success') {
                        setTimeout(() => {
                            window.location.href = '/paper/list/';
                        }, 1500);
                    } else {
                        toast(response.data.message || 'Failed to update paper', { type: "error" });
                    }
                }).catch(() => {
                    showLoading(false);
                });
                return;
            }

            // Create mode - existing logic
            const params = new URLSearchParams();
            params.append('userid_code', formData.get('userid_code'));
            params.append('name', formData.get('name'));
            params.append('email', formData.get('email'));
            params.append('phone', formData.get('phone'));
            params.append('organization', formData.get('organization'));

            const paramsString = params.toString();
            fetchUrl = inGroupCategory === 'Group' ? '/savePaperGroup?' + paramsString : '/savePaperOne?' + paramsString;

            myApi.post(fetchUrl, formData, "multipart/form-data" )
            .then(() => {
                showLoading(false);
                setTimeout(() => {
                    window.location.href = '/paper/list/';
                }, 1500);
            }).catch(() => {
                showLoading(false);
            });
        } catch (error) {
            showLoading(false);
            toast(error.message, { type: "error" });
        }

    }

    const handleSelectionChange = (e) => {
        let sb = e.target;
        if (sb.id === 's-theme') {
            setInTheme(e.target.value);
            setInSubTheme(document.getElementById('s-subtheme').value);
            setInCategory('');
            setInGroupCategory('');
        }

        if (sb.id === 's-subtheme') {
            setInSubTheme(e.target.value);
            setInCategory(document.getElementById('s-category').value);
            setInGroupCategory('');
        }

        if (sb.id === 's-category') {
            setInCategory(e.target.value);
            setInGroupCategory(document.getElementById('s-group').value);
        }

        if (sb.id === 's-group') {
            setInGroupCategory(e.target.value);

            if (e.target.value !== '' && e.target.value !== 'Group') {
                setFormValues(prev => ({
                    ...prev,
                    nama_2: '',
                    email_2: '',
                    instansi_2: '',
                    address_2: '',
                    phone_2: '',
                    nama_3: '',
                    email_3: '',
                    instansi_3: '',
                    address_3: '',
                    phone_3: ''
                }));
            }
        }

        if (sb.id === 's-title') {
            if (e.target.value === '' && !editMode) {
                setAgreementChecked(false);
            }
        }

        if (sb.id === 's-agreement') {
            setAgreementChecked(e.target.checked);
        }
    };

    // Real-time validation handlers
    const handleTitleChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, judul: value }));
        setFormValues(prev => ({ ...prev, judul: value }));
        handleSelectionChange(e);
        if (value) validateTitleField(value);
    };

    const handleFileChange = (e, fieldName, fieldNameLabel) => {
        const file = e.target.files[0];
        if (file) validateFileField(file, fieldName, fieldNameLabel);
    };

    const handleAuthorChange = (e, fieldName, fieldNameLabel) => {
        const value = e.target.value;
        // Treat "-" and "-" as empty, skip validation
        const cleanedValue = (value === '-' || value === '-') ? '' : value;
        if (cleanedValue) validateAuthorField(cleanedValue, fieldName, fieldNameLabel);
    };

    const handleThirdAuthorChange = (e, fieldName) => {
        const value = e.target.value;
        const cleanedValue = (value === '-' || value === '-') ? '' : value;

        if (!cleanedValue) {
            setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
            return;
        }

        let validation;
        if (fieldName.includes('email')) {
            validation = validateEmail(cleanedValue);
        } else if (fieldName.includes('address')) {
            validation = validateText(cleanedValue, {
                minLength: 1, maxLength: 500, fieldName: 'Third author address'
            });
        } else {
            validation = validateAuthorName(cleanedValue);
        }

        setValidationErrors(prev => ({
            ...prev,
            [fieldName]: validation.valid ? '' : validation.message
        }));
    };

    const handlePhoneChange = (e, fieldName) => {
        const value = e.target.value;
        if (value) {
            const validation = validatePhone(value);
            setValidationErrors(prev => ({
                ...prev,
                [fieldName]: validation.valid ? '' : validation.message
            }));
        }
    };
      
    return (
        <>
            {isLoadingPaper && (
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading paper data...</p>
                </div>
            )}
            {!isLoadingPaper && (
            <Card className="bg-white border-0 rounded-3 p-3">
                <Card.Header>
                    <Card.Title className="mb-0">
                        {editMode ? `Edit Paper: ${paperCode}` : 'Add New Paper'}
                    </Card.Title>
                </Card.Header>
                <Card.Body className="p-4">
                    <Form id="form-create" key={formKey} className="row g-3" onSubmit={handleSubmit}>
                        <Form.Control type="hidden" name="userid_code" id="userid_code" value="" />
                        <Form.Control type="hidden" name="name" id="name" value="" />
                        <Form.Control type="hidden" name="email" id="email" value={formData.email} />
                        <Form.Control type="hidden" name="phone" id="phone" value={formData.phone} />
                        <Form.Control type="hidden" name="organization" id="organization" value={formData.organization} />
                        <Form.Control type="hidden" name="paper_type" id="paper_type" value={inTheme === 'General' ? 'General Paper' : inTheme} />
                        <Form.Control type="hidden" name="original_paper_type" id="original_paper_type" value={existingPaperData?.paper_type || ''} />
                        <Form.Control type="hidden" name="paper_code" id="paper_code" value={paperCode} />

                        <Form.Group className="col-md-6">
                            <Form.Label className="label text-secondary">Theme</Form.Label>
                            <FloatingLabel label="Theme" className="mb-3">
                                <Form.Select id="s-theme" name="jenis_paper_text" onChange={handleSelectionChange} value={inTheme} required>
                                    <option value="">Pilih Tema</option>
                                    {
                                        Object.keys(categoryBase).map((key, index) => {
                                            const displayText = key === "General" ? "Mengakselerasi Pembangunan Wilayah untuk Pertumbuhan Ekonomi Jawa Timur yang Unggul, Inklusif serta Berkelanjutan Menuju Ketahanan dan Kemandirian Ekonomi Nasional" : key;
                                            return (
                                                <option key={key} value={key} disabled={key !== "General" ? true : false}>{displayText}</option>
                                            )
                                        })
                                    }
                                </Form.Select>
                            </FloatingLabel>
                        </Form.Group>
                        <Form.Group className="col-md-6">
                            <Form.Label className="label text-secondary">Sub Theme</Form.Label>
                            <FloatingLabel label="Sub Theme" className="mb-3">
                                <Form.Select id="s-subtheme" name="sub_tema_text" onChange={handleSelectionChange} disabled={ inTheme === '' ? true : false } value={inSubTheme} required>
                                    <option value="">Pilih Sub Tema</option>
                                    {
                                        inTheme !== "" && categoryBase[inTheme] && categoryBase[inTheme].map((key, index) => {
                                            const showOption = key.active === undefined || key.active === true;
                                            return showOption ? (
                                                <option key={key.text || index} value={key.text}>{key.text}</option>
                                            ) : null;
                                        }).filter(Boolean)
                                    }
                                </Form.Select>
                            </FloatingLabel>
                        </Form.Group>
                        <Form.Group className="col-md-6">
                            <Form.Label className="label text-secondary">Category</Form.Label>
                            <FloatingLabel label="Category" className="mb-3">
                                <Form.Select id="s-category" name="kategori" onChange={handleSelectionChange} disabled={ inSubTheme === '' ? true : false } value={inCategory} required>
                                    <option value="">Pilih Kategori</option>
                                    {
                                        inSubTheme !== "" && Object.keys(userCategory).map((key, index) => {
                                            return (
                                                <option key={key} value={key}>{categoryLabels[key]}</option>
                                            )
                                        })
                                    }
                                </Form.Select>
                            </FloatingLabel>
                        </Form.Group>
                        <Form.Group className="col-md-6">
                            <Form.Label className="label text-secondary">Jenis Kepesertaan</Form.Label>
                            <FloatingLabel label="Group" className="mb-3">
                                <Form.Select id="s-group" name="keikutsertaan" onChange={handleSelectionChange} disabled={ inCategory === '' ? true : false } value={inGroupCategory} required>
                                    <option value="">Pilih Jenis Kepesertaan</option>
                                    {
                                        inCategory !== "" && userCategory[inCategory].map((key, index) => {
                                            return (
                                                <option key={key.value || index} value={key.value}>{key.value}</option>
                                            )
                                        })
                                    }
                                </Form.Select>
                            </FloatingLabel>
                        </Form.Group>

                        <Form.Group className="col-sm-12 mb-3">
                            <Form.Label className="label text-secondary">Judul Paper</Form.Label>
                            <Form.Control 
                                id="s-title" 
                                as="textarea" 
                                name="judul" 
                                rows={5} 
                                value={formValues.judul}
                                className={`form-control ${validationErrors.judul ? 'is-invalid' : ''}`} 
                                placeholder="Write your paper title in here (minimum 10 characters)" 
                                onChange={handleTitleChange}
                                onKeyUp={handleTitleChange} 
                                required 
                            />
                            {validationErrors.judul && (
                                <Form.Control.Feedback type="invalid">{validationErrors.judul}</Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <div className="row">
                            <Form.Group className="col-lg-6 col-sm-12 text-center p-3">
                                <Form.Label className="label">Upload File Paper {editMode ? '' : <span className="text-danger">*</span>}</Form.Label>
                                <Form.Text className="text-muted d-block mb-2">PDF or DOC, max 5MB {editMode ? '(Optional - leave empty to keep existing file)' : ''}</Form.Text>
                                <input 
                                    type="file" 
                                    name="paper_file" 
                                    className={`form-control ${fileErrors.paper_file ? 'is-invalid' : ''}`} 
                                    placeholder="Upload your paper" 
                                    onChange={(e) => handleFileChange(e, 'paper_file', 'Paper File')}
                                    accept=".pdf,.doc,.docx"
                                    required={!editMode} 
                                />
                                {fileErrors.paper_file && (
                                    <Form.Control.Feedback type="invalid">{fileErrors.paper_file}</Form.Control.Feedback>
                                )}
                                <ExistingFilePreview 
                                    filePath={existingPaperData?.paper_filePath_1} 
                                    fileName={existingPaperData?.paper_fileName_1}
                                    label="Paper"
                                    editMode={editMode}
                                />
                            </Form.Group>
                            <Form.Group className="col-lg-6 col-sm-12 text-center p-3">
                                <Form.Label className="label">Upload CV Anda {editMode ? '' : <span className="text-danger">*</span>}</Form.Label>
                                <Form.Text className="text-muted d-block mb-2">PDF or DOC, max 5MB {editMode ? '(Optional - leave empty to keep existing file)' : ''}</Form.Text>
                                <input 
                                    type="file" 
                                    name="cv_file" 
                                    className={`form-control ${fileErrors.cv_file ? 'is-invalid' : ''}`} 
                                    placeholder="Upload your CV" 
                                    onChange={(e) => handleFileChange(e, 'cv_file', 'CV File')}
                                    accept=".pdf,.doc,.docx"
                                    required={!editMode} 
                                />
                                {fileErrors.cv_file && (
                                    <Form.Control.Feedback type="invalid">{fileErrors.cv_file}</Form.Control.Feedback>
                                )}
                                <ExistingFilePreview 
                                    filePath={existingPaperData?.cv_filePath_1} 
                                    fileName={existingPaperData?.cv_fileName_1}
                                    label="CV"
                                    editMode={editMode}
                                />
                            </Form.Group>
                            <Form.Group className="col-lg-6 col-sm-12 text-center p-3">
                                <Form.Label className="label">Upload Surat Pernyataan {editMode ? '' : <span className="text-danger">*</span>}</Form.Label>
                                <Form.Text className="text-muted d-block mb-2">PDF or DOC, max 5MB {editMode ? '(Optional - leave empty to keep existing file)' : ''}</Form.Text>
                                <input 
                                    type="file" 
                                    name="pernyataan_file" 
                                    className={`form-control ${fileErrors.pernyataan_file ? 'is-invalid' : ''}`} 
                                    placeholder="Upload Surat Pernyataan" 
                                    onChange={(e) => handleFileChange(e, 'pernyataan_file', 'Statement Letter')}
                                    accept=".pdf,.doc,.docx"
                                    required={!editMode} 
                                />
                                {fileErrors.pernyataan_file && (
                                    <Form.Control.Feedback type="invalid">{fileErrors.pernyataan_file}</Form.Control.Feedback>
                                )}
                                <ExistingFilePreview 
                                    filePath={existingPaperData?.pernyataan_filePath_1} 
                                    fileName={existingPaperData?.pernyataan_fileName_1}
                                    label="Surat Pernyataan"
                                    editMode={editMode}
                                />
                            </Form.Group>
                            <Form.Group className={"col-lg-6 col-sm-12 text-center p-3 " + (inCategory === "Mahasiswa" ? '' : 'd-none')}>
                                <Form.Label className="label">Upload bukti mahasiswa aktif PDDIKTI {editMode ? '' : <span className="text-danger">*</span>}</Form.Label>
                                <Form.Text className="text-muted d-block mb-2">PDF or DOC, max 5MB {editMode ? '(Optional - leave empty to keep existing file)' : ''}</Form.Text>
                                <input 
                                    type="file" 
                                    name="pddikti_file" 
                                    className={`form-control ${fileErrors.pddikti_file ? 'is-invalid' : ''}`} 
                                    placeholder="Upload Bukti PDDIKTI" 
                                    onChange={(e) => handleFileChange(e, 'pddikti_file', 'PDDIKTI Proof')}
                                    accept=".pdf,.doc,.docx"
                                    required={inCategory === "Mahasiswa" && !editMode} 
                                    disabled={inCategory === "Mahasiswa" ? false : true} 
                                />
                                {fileErrors.pddikti_file && (
                                    <Form.Control.Feedback type="invalid">{fileErrors.pddikti_file}</Form.Control.Feedback>
                                )}
                                <ExistingFilePreview 
                                    filePath={existingPaperData?.pddikti_filePath_1} 
                                    fileName={existingPaperData?.pddikti_fileName_1}
                                    label="PDDIKTI"
                                    editMode={editMode}
                                />
                            </Form.Group>
                        </div>

                        <hr />

                        {inGroupCategory === 'Group' ? (
                            <div className="text-center" id="groupForm">
                                <h5 className="fs-16 p-3">Tim Penulis</h5>
                                <p className="bg-info text-white p-2">Field Penulis Kedua Wajib Diisi, Sedangkan Field Penulis Ketiga Opsional</p>
                                <div className="d-flex flex-wrap justify-content-center gap-2 p-1 rounded-3 bg-secondary">
                                    <div className="flex-fill m-2 p-2">
<p className="text-white mb-3 text-center required">Penulis Kedua</p>
                                        <FloatingLabel label="Name" className="mb-3">
                                            <Form.Control 
                                                className={`form-additional ${validationErrors.nama_2 ? 'is-invalid' : ''}`} 
                                                type="text" 
                                                id="s-name-2" 
                                                name="nama_2" 
                                                value={formValues.nama_2}
                                                placeholder="Type your name" 
                                                onChange={(e) => {
                                                    setFormValues(prev => ({ ...prev, nama_2: e.target.value }));
                                                    handleAuthorChange(e, 'nama_2', 'Second Author Name');
                                                }}
                                            />
                                        </FloatingLabel>
                                        {validationErrors.nama_2 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.nama_2}</Form.Control.Feedback>
                                        )}
                                        <FloatingLabel label="Email" className="mb-3">
                                            <Form.Control
                                                className={`form-additional ${validationErrors.email_2 ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="s-email-2"
                                                name="email_2"
                                                value={formValues.email_2}
                                                placeholder="Type your email"
                                                onChange={(e) => {
                                                    setFormValues(prev => ({ ...prev, email_2: e.target.value }));
                                                    handleAuthorChange(e, 'email_2', 'Second Author Email');
                                                }}
                                            />
                                        </FloatingLabel>
                                        {validationErrors.email_2 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.email_2}</Form.Control.Feedback>
                                        )}
                                        <FloatingLabel label="Instansi" className="mb-3">
                                            <Form.Control
                                                className={`form-additional ${validationErrors.instansi_2 ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="s-instansi-2"
                                                name="instansi_2"
                                                value={formValues.instansi_2}
                                                placeholder="Type your instansi"
                                                onChange={(e) => setFormValues(prev => ({ ...prev, instansi_2: e.target.value }))}
                                            />
                                        </FloatingLabel>
                                        {validationErrors.instansi_2 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.instansi_2}</Form.Control.Feedback>
                                        )}
                                        <FloatingLabel label="Alamat Domisili" className="mb-3">
                                            <Form.Control
                                                className={`form-additional ${validationErrors.address_2 ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="s-address-2"
                                                name="address_2"
                                                value={formValues.address_2}
                                                placeholder="Type your address"
                                                onChange={(e) => {
                                                    setFormValues(prev => ({ ...prev, address_2: e.target.value }));
                                                    handleAuthorChange(e, 'address_2', 'Second Author Address');
                                                }}
                                            />
                                        </FloatingLabel>
                                        {validationErrors.address_2 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.address_2}</Form.Control.Feedback>
                                        )}
                                        <FloatingLabel label="Handphone Number" className="mb-3">
                                            <Form.Control
                                                className={`form-additional ${validationErrors.phone_2 ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="s-handphone-2"
                                                name="phone_2"
                                                value={formValues.phone_2}
                                                placeholder="Type your handball number (e.g., 081234567890)"
                                                onChange={(e) => {
                                                    setFormValues(prev => ({ ...prev, phone_2: e.target.value }));
                                                    handlePhoneChange(e, 'phone_2');
                                                }}
/>
                                        </FloatingLabel>
                                        <Form.Group className="mb-3">
                                            <label className="label text-white mt-2">Upload CV {!editMode && <span className="text-danger">*</span>}</label>
                                            <Form.Text className="text-muted d-block mb-2">PDF or DOC, max 5MB {editMode ? '(Optional - leave empty to keep existing file)' : ''}</Form.Text>
                                            <Form.Control 
                                                type="file" 
                                                id="s-file-cv-2" 
                                                name="cv_2_file" 
                                                className={`form-additional form-control ${fileErrors.cv_2_file ? 'is-invalid' : ''}`} 
                                                placeholder="Upload Bukti CV" 
                                                onChange={(e) => handleFileChange(e, 'cv_2_file', 'Second Author CV')}
                                                accept=".pdf,.doc,.docx"
                                                required={!editMode}
                                            />
                                            {fileErrors.cv_2_file && (
                                                <Form.Control.Feedback type="invalid">{fileErrors.cv_2_file}</Form.Control.Feedback>
                                            )}
                                            <ExistingFilePreview 
                                                filePath={existingPaperData?.cv_filePath_2} 
                                                fileName={existingPaperData?.cv_fileName_2}
                                                label="CV"
                                                editMode={editMode}
                                            />
                                        </Form.Group>
                                        <Form.Group className={`mb-3 ${inCategory === "Mahasiswa" ? '' : 'd-none'}`}>
                                            <label className="label text-white mt-2">Bukti PDDIKTI {inCategory === "Mahasiswa" && !editMode && <span className="text-danger">*</span>}</label>
                                            <Form.Text className="text-muted d-block mb-2">PDF or DOC, max 5MB {editMode ? '(Optional - leave empty to keep existing file)' : ''}</Form.Text>
                                            <Form.Control 
                                                type="file" 
                                                id="s-file-pddikti-2" 
                                                name="pddikti_2_file" 
                                                className={`form-additional form-control ${fileErrors.pddikti_2_file ? 'is-invalid' : ''}`} 
                                                placeholder="Upload Bukti PDDIKTI" 
                                                onChange={(e) => handleFileChange(e, 'pddikti_2_file', 'Second Author PDDIKTI')}
                                                accept=".pdf,.doc,.docx"
                                                required={inCategory === "Mahasiswa" && !editMode} 
                                            />
                                            {fileErrors.pddikti_2_file && (
                                                <Form.Control.Feedback type="invalid">{fileErrors.pddikti_2_file}</Form.Control.Feedback>
                                            )}
                                            <ExistingFilePreview 
                                                filePath={existingPaperData?.pddikti_filePath_2} 
                                                fileName={existingPaperData?.pddikti_fileName_2}
                                                label="PDDIKTI"
                                                editMode={editMode}
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="flex-fill m-2 p-2">
                                        <p className="text-white mb-3 text-center required">Penulis Ketiga (Optional)</p>
                                        <FloatingLabel label="Name" className="mb-3">
                                            <Form.Control 
                                                className={`form-additional ${validationErrors.nama_3 ? 'is-invalid' : ''}`} 
                                                type="text" 
                                                id="s-name-3" 
                                                name="nama_3" 
                                                value={formValues.nama_3}
                                                placeholder="Type your name" 
                                                onChange={(e) => {
                                                    setFormValues(prev => ({ ...prev, nama_3: e.target.value }));
                                                    handleThirdAuthorChange(e, 'nama_3');
                                                }}
                                            />
                                        </FloatingLabel>
                                        {validationErrors.nama_3 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.nama_3}</Form.Control.Feedback>
                                        )}
                                        <FloatingLabel label="Email" className="mb-3">
                                            <Form.Control
                                                className={`form-additional ${validationErrors.email_3 ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="s-email-3"
                                                name="email_3"
                                                value={formValues.email_3}
                                                placeholder="Type your email"
                                                onChange={(e) => {
                                                    setFormValues(prev => ({ ...prev, email_3: e.target.value }));
                                                    handleThirdAuthorChange(e, 'email_3');
                                                }}
                                            />
                                        </FloatingLabel>
                                        {validationErrors.email_3 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.email_3}</Form.Control.Feedback>
                                        )}
                                        <FloatingLabel label="Instansi" className="mb-3">
                                            <Form.Control
                                                className={`form-additional ${validationErrors.instansi_3 ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="s-instansi-3"
                                                name="instansi_3"
                                                value={formValues.instansi_3}
                                                placeholder="Type your instansi"
                                                onChange={(e) => setFormValues(prev => ({ ...prev, instansi_3: e.target.value }))}
                                            />
                                        </FloatingLabel>
                                        {validationErrors.instansi_3 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.instansi_3}</Form.Control.Feedback>
                                        )}
                                        <FloatingLabel label="Alamat Domisili" className="mb-3">
                                            <Form.Control
                                                className={`form-additional ${validationErrors.address_3 ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="s-address-3"
                                                name="address_3"
                                                value={formValues.address_3}
                                                placeholder="Type your address"
                                                onChange={(e) => {
                                                    setFormValues(prev => ({ ...prev, address_3: e.target.value }));
                                                    handleThirdAuthorChange(e, 'address_3');
                                                }}
                                            />
                                        </FloatingLabel>
                                        {validationErrors.address_3 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.address_3}</Form.Control.Feedback>
                                        )}
                                        <FloatingLabel label="Handphone Number" className="mb-3">
                                            <Form.Control
                                                className={`form-additional ${validationErrors.phone_3 ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="s-handphone-3"
                                                name="phone_3"
                                                value={formValues.phone_3}
                                                placeholder="Type your handball number (e.g., 081234567890)"
                                                onChange={(e) => {
                                                    setFormValues(prev => ({ ...prev, phone_3: e.target.value }));
                                                    handlePhoneChange(e, 'phone_3');
                                                }}
/>
                                        </FloatingLabel>
                                        {validationErrors.phone_3 && (
                                            <Form.Control.Feedback type="invalid">{validationErrors.phone_3}</Form.Control.Feedback>
                                        )}
                                        <Form.Group className="mb-3">
                                            <label className="label text-white mt-2">Upload CV</label>
                                            <Form.Text className="text-muted d-block mb-2">PDF or DOC, max 5MB {editMode ? '(Optional - leave empty to keep existing file)' : ''}</Form.Text>
                                            <Form.Control 
                                                type="file" 
                                                id="s-file-cv-3" 
                                                name="cv_3_file" 
                                                className={`form-additional form-control ${fileErrors.cv_3_file ? 'is-invalid' : ''}`} 
                                                placeholder="Upload Bukti CV" 
                                                onChange={(e) => handleFileChange(e, 'cv_3_file', 'Third Author CV')}
                                                accept=".pdf,.doc,.docx"
                                            />
                                            {fileErrors.cv_3_file && (
                                                <Form.Control.Feedback type="invalid">{fileErrors.cv_3_file}</Form.Control.Feedback>
                                            )}
                                            <ExistingFilePreview 
                                                filePath={existingPaperData?.cv_filePath_3} 
                                                fileName={existingPaperData?.cv_fileName_3}
                                                label="CV"
                                                editMode={editMode}
                                            />
                                        </Form.Group>
                                        <Form.Group className={`mb-3 ${inCategory === "Mahasiswa" ? '' : 'd-none'}`}>
                                            <label className="label text-white mt-2">Bukti PDDIKTI {inCategory === "Mahasiswa" && !editMode && formValues.nama_3 && formValues.nama_3.trim() !== '' && <span className="text-danger">*</span>}</label>
                                            <Form.Text className="text-muted d-block mb-2">PDF or DOC, max 5MB {editMode ? '(Optional - leave empty to keep existing file)' : ''}</Form.Text>
                                            <Form.Control 
                                                type="file" 
                                                id="s-file-pddikti-3" 
                                                name="pddikti_3_file" 
                                                className={`form-additional form-control ${fileErrors.pddikti_3_file ? 'is-invalid' : ''}`} 
                                                placeholder="Upload Bukti PDDIKTI" 
                                                onChange={(e) => handleFileChange(e, 'pddikti_3_file', 'Third Author PDDIKTI')}
                                                accept=".pdf,.doc,.docx"
                                                required={inCategory === "Mahasiswa" && !editMode && formValues.nama_3 && formValues.nama_3.trim() !== ''} 
                                            />
                                            {fileErrors.pddikti_3_file && (
                                                <Form.Control.Feedback type="invalid">{fileErrors.pddikti_3_file}</Form.Control.Feedback>
                                            )}
                                            <ExistingFilePreview 
                                                filePath={existingPaperData?.pddikti_filePath_3} 
                                                fileName={existingPaperData?.pddikti_fileName_3}
                                                label="PDDIKTI"
                                                editMode={editMode}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>
) : null}
                        <hr />
                        <div className="sticky-bottom d-flex justify-content-center gap-2 p-3 bg-white">
                            <Form.Group className="col-md-3">
                                <Form.Check 
                                    type="checkbox" 
                                    label="I agree to the terms and conditions" 
                                    name="agreement" 
                                    id="s-agreement" 
                                    onChange={handleSelectionChange} 
                                    disabled={editMode ? false : formValues.judul.trim() === ''}
                                    checked={editMode ? true : agreementChecked}
                                    required={!editMode}
                                />
                                {validationErrors.agreement && (
                                    <Form.Control.Feedback type="invalid" className="d-block mt-1">
                                        {validationErrors.agreement}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                            <Button variant="dark" type="submit" id="s-submit-button" disabled={editMode ? false : !(formValues.judul.trim() && agreementChecked)}>
                                <span><i className="material-symbols-outlined fs-12">{editMode ? 'update' : 'save'}</i> {editMode ? 'Update Paper' : 'Save Paper'}</span>
                            </Button>
                            <Button variant="secondary" type="reset" onClick={() => {
                                setValidationErrors({});
                                setFileErrors({});
                            }}>
                                <span><i className="material-symbols-outlined fs-12">cancel</i> Cancel</span>
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
            )}
        </>
    );
}

export default PaperCreate
